import { useState, useEffect } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { customBlocksApi, ApiCustomBlock } from '../../utils/api'
import { useEditorStore } from '../../store/editor.store'

function CustomBlockItem({ block, onEdit }: { block: ApiCustomBlock; onEdit: (b: ApiCustomBlock) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `custom-library-${block.id}`,
    data: { type: 'new-block', blockType: 'custom', customBlockData: makeBlockData(block) },
  })

  function handleClick() {
    const { page, selectedSectionId, addSection, addBlock } = useEditorStore.getState()
    const blockData = makeBlockData(block)
    const targetSection = selectedSectionId
      ? page.sections.find(s => s.id === selectedSectionId)
      : page.sections[page.sections.length - 1]

    if (targetSection) {
      addBlock(targetSection.id, 'custom', blockData)
    } else {
      addSection()
      setTimeout(() => {
        const { page: p, addBlock: ab } = useEditorStore.getState()
        const sec = p.sections[p.sections.length - 1]
        if (sec) ab(sec.id, 'custom', blockData)
      }, 0)
    }
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      style={{
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        background: 'var(--bg-2)',
        borderRadius: '8px',
        cursor: 'grab',
        border: '1px solid var(--border)',
        transition: 'border-color 0.15s',
      }}
    >
      <span style={{ fontSize: '20px', flexShrink: 0 }}>{block.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.name}</div>
        {block.description && <div style={{ fontSize: '10px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.description}</div>}
      </div>
      <button
        onClick={e => { e.stopPropagation(); onEdit(block) }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)', padding: '2px 6px', flexShrink: 0 }}
        title="Редактировать"
      >✏</button>
    </div>
  )
}

function makeBlockData(b: ApiCustomBlock): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}
  for (const f of b.fields) {
    defaults[f.name] = f.default
  }
  return {
    customBlockId: b.id,
    html: b.html,
    css: b.css,
    js: b.js,
    fields: b.fields,
    ...defaults,
  }
}

interface FieldDef { name: string; label: string; default: string }

function BlockEditor({ initial, onSave, onDelete, onClose }: {
  initial?: ApiCustomBlock
  onSave: (data: Partial<ApiCustomBlock>) => Promise<void>
  onDelete?: () => Promise<void>
  onClose: () => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [icon, setIcon] = useState(initial?.icon ?? '🧩')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [html, setHtml] = useState(initial?.html ?? '<div class="my-block">\n  <h2>{{title}}</h2>\n  <p>{{text}}</p>\n</div>')
  const [css, setCss] = useState(initial?.css ?? '.my-block { padding: 60px 24px; text-align: center; }')
  const [fields, setFields] = useState<FieldDef[]>(initial?.fields ?? [
    { name: 'title', label: 'Заголовок', default: 'Мой блок' },
    { name: 'text', label: 'Текст', default: 'Описание блока' },
  ])
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<'html' | 'css' | 'fields'>('html')

  const addField = () => setFields(f => [...f, { name: `field${f.length + 1}`, label: 'Поле', default: '' }])
  const updateField = (i: number, key: keyof FieldDef, val: string) =>
    setFields(f => f.map((fi, idx) => idx === i ? { ...fi, [key]: val } : fi))
  const removeField = (i: number) => setFields(f => f.filter((_, idx) => idx !== i))

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSave({ name, icon, description, html, css, fields })
    } finally {
      setSaving(false)
    }
  }

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '5px 12px',
    fontSize: '11px',
    fontWeight: active ? 700 : 500,
    background: active ? 'var(--accent)' : 'none',
    color: active ? '#fff' : 'var(--text-muted)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          className="prop-input"
          placeholder="Иконка"
          value={icon}
          onChange={e => setIcon(e.target.value)}
          style={{ width: '44px', textAlign: 'center', fontSize: '18px', padding: '4px' }}
        />
        <input className="prop-input" placeholder="Название блока" value={name} onChange={e => setName(e.target.value)} style={{ flex: 1 }} />
      </div>
      <input className="prop-input" placeholder="Описание (необязательно)" value={description} onChange={e => setDescription(e.target.value)} />

      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-3)', borderRadius: '8px', padding: '3px' }}>
        <button style={tabStyle(tab === 'html')} onClick={() => setTab('html')}>HTML</button>
        <button style={tabStyle(tab === 'css')} onClick={() => setTab('css')}>CSS</button>
        <button style={tabStyle(tab === 'fields')} onClick={() => setTab('fields')}>Поля</button>
      </div>

      {tab === 'html' && (
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Используйте <code style={{ background: 'var(--bg-3)', padding: '1px 4px', borderRadius: '3px' }}>{'{{fieldName}}'}</code> для вставки значений полей
          </div>
          <textarea
            className="prop-textarea"
            rows={10}
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
            value={html}
            onChange={e => setHtml(e.target.value)}
          />
        </div>
      )}
      {tab === 'css' && (
        <textarea
          className="prop-textarea"
          rows={10}
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
          value={css}
          onChange={e => setCss(e.target.value)}
        />
      )}
      {tab === 'fields' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {fields.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input className="prop-input" placeholder="name" value={f.name} onChange={e => updateField(i, 'name', e.target.value)} style={{ width: '90px', fontFamily: 'monospace', fontSize: '11px' }} />
              <input className="prop-input" placeholder="Метка" value={f.label} onChange={e => updateField(i, 'label', e.target.value)} style={{ flex: 1 }} />
              <input className="prop-input" placeholder="По умолч." value={f.default} onChange={e => updateField(i, 'default', e.target.value)} style={{ flex: 1 }} />
              <button onClick={() => removeField(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '0 4px', flexShrink: 0 }}>×</button>
            </div>
          ))}
          <button onClick={addField} style={{ padding: '6px', background: 'var(--bg-3)', border: '1px dashed var(--border)', borderRadius: '6px', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>
            + Добавить поле
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '4px' }}>
        {onDelete && (
          <button onClick={onDelete} style={{ padding: '7px 14px', background: 'none', border: '1px solid #ef444444', color: '#ef4444', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>
            Удалить
          </button>
        )}
        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', background: 'var(--bg-3)', border: 'none', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
            Отмена
          </button>
          <button onClick={handleSave} disabled={saving || !name.trim()} style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: saving || !name.trim() ? 0.6 : 1 }}>
            {initial ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  )
}

function MarketplaceTab() {
  const [blocks, setBlocks] = useState<ApiCustomBlock[]>([])
  const [loading, setLoading] = useState(true)
  const [installing, setInstalling] = useState<string | null>(null)
  const [installed, setInstalled] = useState<Set<string>>(new Set())

  useEffect(() => {
    setLoading(true)
    customBlocksApi.marketplace().then(setBlocks).catch(() => setBlocks([])).finally(() => setLoading(false))
  }, [])

  const install = async (block: ApiCustomBlock) => {
    setInstalling(block.id)
    try {
      await customBlocksApi.create({
        name: block.name,
        icon: block.icon,
        description: block.description,
        html: block.html,
        css: block.css,
        js: block.js,
        fields: block.fields,
        public: false,
      })
      setInstalled(prev => new Set([...prev, block.id]))
    } catch { /* ignore */ } finally {
      setInstalling(null)
    }
  }

  if (loading) return <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Загрузка...</div>
  if (blocks.length === 0) return (
    <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)', fontSize: '12px' }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏪</div>
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>Маркетплейс пуст</div>
      <div>Публичных блоков пока нет</div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {blocks.map(b => (
        <div key={b.id} style={{ padding: '10px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>{b.icon}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                {b.description && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.description}</div>}
              </div>
            </div>
            <button
              onClick={() => install(b)}
              disabled={installing === b.id || installed.has(b.id)}
              style={{ flexShrink: 0, padding: '5px 10px', background: installed.has(b.id) ? 'var(--bg-3)' : 'var(--accent)', color: installed.has(b.id) ? 'var(--text-muted)' : '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: installed.has(b.id) ? 'default' : 'pointer' }}
            >
              {installing === b.id ? '...' : installed.has(b.id) ? '✓' : '+ Установить'}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

export function CustomBlocksPanel() {
  const [blocks, setBlocks] = useState<ApiCustomBlock[]>([])
  const [editing, setEditing] = useState<ApiCustomBlock | null | 'new'>(null)
  const [view, setView] = useState<'mine' | 'marketplace'>('mine')

  const load = () => { customBlocksApi.list().then(setBlocks).catch(() => {}) }
  useEffect(() => { load() }, [])

  const handleCreate = async (data: Partial<ApiCustomBlock>) => {
    await customBlocksApi.create(data as Parameters<typeof customBlocksApi.create>[0])
    load()
    setEditing(null)
  }

  const handleUpdate = async (id: string, data: Partial<ApiCustomBlock>) => {
    await customBlocksApi.update(id, data)
    load()
    setEditing(null)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить этот блок?')) return
    await customBlocksApi.delete(id)
    load()
    setEditing(null)
  }

  if (editing !== null) {
    return (
      <div style={{ padding: '12px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
          {editing === 'new' ? '+ Новый блок' : `Редактирование: ${editing.name}`}
        </div>
        <BlockEditor
          initial={editing === 'new' ? undefined : editing}
          onSave={data => editing === 'new' ? handleCreate(data) : handleUpdate(editing.id, data)}
          onDelete={editing !== 'new' ? () => handleDelete(editing.id) : undefined}
          onClose={() => setEditing(null)}
        />
      </div>
    )
  }

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '6px',
    background: active ? 'var(--accent)' : 'var(--bg-3)',
    color: active ? '#fff' : 'var(--text-muted)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-2)', padding: '3px', borderRadius: '8px' }}>
          <button style={tabBtnStyle(view === 'mine')} onClick={() => setView('mine')}>🧩 Мои</button>
          <button style={tabBtnStyle(view === 'marketplace')} onClick={() => setView('marketplace')}>🏪 Маркетплейс</button>
        </div>
        {view === 'mine' && (
          <button
            onClick={() => setEditing('new')}
            style={{ width: '100%', padding: '7px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          >
            <span style={{ fontSize: '14px' }}>+</span> Создать блок
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
        {view === 'marketplace' ? (
          <MarketplaceTab />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {blocks.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)', fontSize: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🧩</div>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Нет пользовательских блоков</div>
                <div>Создайте свой первый блок из HTML + CSS</div>
              </div>
            )}
            {blocks.map(b => (
              <CustomBlockItem key={b.id} block={b} onEdit={() => setEditing(b)} />
            ))}
          </div>
        )}
      </div>

      {view === 'mine' && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
          {blocks.length} пользовательских блоков
        </div>
      )}
    </div>
  )
}
